import { z } from 'zod';
import { E164PhoneSchema, TimestampSchema, UuidSchema } from './common';

export const AIAgentModelSchema = z.enum([
  'gpt-5-nano',
  'gpt-5-mini',
  'gpt-5',
  'claude-haiku-4-5',
  'claude-sonnet-4-6',
]);
export type AIAgentModel = z.infer<typeof AIAgentModelSchema>;

export const AIToolKindSchema = z.enum([
  'notify-attendant',
  'fetch-knowledge',
  'http-request',
  'tag-lead',
]);
export type AIToolKind = z.infer<typeof AIToolKindSchema>;

export const AIToolFewShotSchema = z.object({
  positive: z.array(z.string().min(1)).min(1),
  negative: z.array(z.string().min(1)),
});
export type AIToolFewShot = z.infer<typeof AIToolFewShotSchema>;

const baseToolFields = {
  name: z.string().min(1).max(80),
  description: z.string().min(1).max(500),
  fewShot: AIToolFewShotSchema,
};

export const AIToolNotifyAttendantSchema = z.object({
  kind: z.literal('notify-attendant'),
  ...baseToolFields,
  notifyTo: z.object({
    phone: E164PhoneSchema,
  }),
});
export type AIToolNotifyAttendant = z.infer<typeof AIToolNotifyAttendantSchema>;

export const AIToolFetchKnowledgeSchema = z.object({
  kind: z.literal('fetch-knowledge'),
  ...baseToolFields,
  knowledgeBaseId: UuidSchema,
});
export type AIToolFetchKnowledge = z.infer<typeof AIToolFetchKnowledgeSchema>;

export const AIToolHttpRequestSchema = z.object({
  kind: z.literal('http-request'),
  ...baseToolFields,
  method: z.enum(['GET', 'POST']),
  url: z.string().url(),
  headers: z.record(z.string(), z.string()).optional(),
  bodyTemplate: z.string().optional(),
});
export type AIToolHttpRequest = z.infer<typeof AIToolHttpRequestSchema>;

export const AIToolTagLeadSchema = z.object({
  kind: z.literal('tag-lead'),
  ...baseToolFields,
  tagsToAdd: z.array(z.string().min(1)).min(1),
});
export type AIToolTagLead = z.infer<typeof AIToolTagLeadSchema>;

export const AIToolSchema = z.discriminatedUnion('kind', [
  AIToolNotifyAttendantSchema,
  AIToolFetchKnowledgeSchema,
  AIToolHttpRequestSchema,
  AIToolTagLeadSchema,
]);
export type AITool = z.infer<typeof AIToolSchema>;

export const AIAgentHandoffPolicySchema = z.object({
  maxFailedAttempts: z.number().int().positive(),
  keywordsForHandoff: z.array(z.string().min(1)).optional(),
});
export type AIAgentHandoffPolicy = z.infer<typeof AIAgentHandoffPolicySchema>;

export const AIAgentSchema = z.object({
  id: UuidSchema,
  tenantId: UuidSchema,
  name: z.string().min(1).max(100),
  model: AIAgentModelSchema,
  apiKeyMasked: z.string(),
  systemPrompt: z.string().min(10).max(8000),
  tools: z.array(AIToolSchema),
  handoffPolicy: AIAgentHandoffPolicySchema,
  isActive: z.boolean(),
  totalConversations: z.number().int().nonnegative(),
  handoffRate: z.number().min(0).max(1),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type AIAgent = z.infer<typeof AIAgentSchema>;

export const CreateAIAgentInputSchema = z.object({
  name: z.string().min(1).max(100),
  model: AIAgentModelSchema,
  apiKey: z.string().min(20),
  systemPrompt: z.string().min(10).max(8000),
  tools: z.array(AIToolSchema),
  handoffPolicy: AIAgentHandoffPolicySchema,
  isActive: z.boolean(),
});
export type CreateAIAgentInput = z.infer<typeof CreateAIAgentInputSchema>;

export const UpdateAIAgentInputSchema = CreateAIAgentInputSchema.partial();
export type UpdateAIAgentInput = z.infer<typeof UpdateAIAgentInputSchema>;

export const AIAgentsListResponseSchema = z.object({
  agents: z.array(AIAgentSchema),
});
export type AIAgentsListResponse = z.infer<typeof AIAgentsListResponseSchema>;

export const TestAIAgentInputSchema = z.object({
  message: z.string().min(1),
});
export type TestAIAgentInput = z.infer<typeof TestAIAgentInputSchema>;

export const TestAIAgentResponseSchema = z.object({
  reply: z.string(),
  toolsTriggered: z.array(
    z.object({
      kind: AIToolKindSchema,
      name: z.string(),
    }),
  ),
  handoffRequested: z.boolean(),
  /**
   * `true` quando a resposta veio do fallback determinístico (OpenAI fail/sem
   * key configurada). UI mostra banner "Demo mode" condicional.
   */
  fromMock: z.boolean(),
});
export type TestAIAgentResponse = z.infer<typeof TestAIAgentResponseSchema>;

export const GeneratePromptInputSchema = z.object({
  description: z.string().min(10).max(500),
});
export type GeneratePromptInput = z.infer<typeof GeneratePromptInputSchema>;

export const GeneratePromptResponseSchema = z.object({
  prompt: z.string(),
});
export type GeneratePromptResponse = z.infer<typeof GeneratePromptResponseSchema>;
